import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, LogOut, Lock, Settings as SettingsIcon, Loader2, Mail, ChevronUp, ChevronDown, ArrowUpToLine, Package, Percent, PackageX } from "lucide-react";
import { api, TOKEN_KEY, absUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useToast } from "../hooks/use-toast";
import ProductForm from "../components/admin/ProductForm";
import SettingsForm from "../components/admin/SettingsForm";
import SubscribersPanel from "../components/admin/SubscribersPanel";

export default function Admin() {
  const { settings } = useSettings();
  const { toast } = useToast();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSubs, setShowSubs] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [stats, setStats] = useState(null);

  const loadProducts = useCallback(() => {
    api.getProducts().then(setProducts).catch(() => setProducts([]));
    api.getStats().then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .verify()
      .then(() => {
        setAuthed(true);
        loadProducts();
      })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, [loadProducts]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { token } = await api.login(e.target.elements.user.value, e.target.elements.pass.value);
      localStorage.setItem(TOKEN_KEY, token);
      setAuthed(true);
      loadProducts();
    } catch (err) {
      toast({ title: "Login failed", description: "Wrong username or password." });
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setShowForm(true); };

  const removeProduct = async (id) => {
    try {
      await api.deleteProduct(id);
      toast({ title: "Deleted", description: "Product removed." });
      loadProducts();
    } catch (err) {
      toast({ title: "Error", description: "Could not delete." });
    }
  };

  const onSaved = () => { setShowForm(false); setEditing(null); loadProducts(); };

  const applyOrder = async (newList) => {
    setProducts(newList);
    try {
      await api.reorderProducts(newList.map((p) => p.id));
    } catch {
      toast({ title: "שגיאה", description: "לא ניתן לשמור את הסדר." });
      loadProducts();
    }
  };

  const moveProduct = (index, dir) => {
    const j = index + dir;
    if (!products || j < 0 || j >= products.length) return;
    const arr = [...products];
    [arr[index], arr[j]] = [arr[j], arr[index]];
    applyOrder(arr);
  };

  const moveToTop = (index) => {
    if (!products || index === 0) return;
    const arr = [...products];
    const [item] = arr.splice(index, 1);
    arr.unshift(item);
    applyOrder(arr);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <form onSubmit={login} className="w-full max-w-sm bg-white border border-neutral-200 p-8 shadow-sm">
          <div className="text-center mb-8">
            <Link to="/" className="font-display text-2xl tracking-[0.4em]">{settings.brandName}</Link>
            <div className="mt-4 flex items-center justify-center gap-2 text-neutral-500">
              <Lock className="h-4 w-4" />
              <span className="text-xs tracking-[0.2em]">ADMIN PANEL</span>
            </div>
          </div>
          <label className="block text-xs tracking-widest text-neutral-500 mb-1">USERNAME</label>
          <input name="user" required className="w-full border border-neutral-300 px-3 py-2.5 text-sm mb-4 outline-none focus:border-black" />
          <label className="block text-xs tracking-widest text-neutral-500 mb-1">PASSWORD</label>
          <input name="pass" type="password" required className="w-full border border-neutral-300 px-3 py-2.5 text-sm mb-6 outline-none focus:border-black" />
          <button type="submit" disabled={loggingIn} className="w-full bg-black text-white py-3 text-xs tracking-[0.2em] hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />} LOG IN
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="mx-auto max-w-[1200px] px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-display text-xl tracking-[0.35em]">{settings.brandName}</Link>
            <span className="text-xs tracking-[0.2em] text-neutral-400 hidden sm:inline">ADMIN</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setShowSubs(true)} className="flex items-center gap-1.5 text-xs tracking-[0.15em] text-neutral-600 hover:text-black">
              <Mail className="h-4 w-4" /> <span className="hidden sm:inline">SUBSCRIBERS</span>
            </button>
            <button onClick={() => setShowSettings(true)} className="flex items-center gap-1.5 text-xs tracking-[0.15em] text-neutral-600 hover:text-black">
              <SettingsIcon className="h-4 w-4" /> <span className="hidden sm:inline">SETTINGS</span>
            </button>
            <Link to="/" className="text-xs tracking-[0.15em] text-neutral-600 hover:text-black hidden sm:inline">VIEW STORE</Link>
            <button onClick={logout} className="flex items-center gap-1.5 text-xs tracking-[0.15em] text-neutral-600 hover:text-black">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">LOGOUT</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-4 md:px-8 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
            <div className="bg-white border border-neutral-200 p-4 flex items-center gap-3" data-testid="stat-products">
              <Package className="h-5 w-5 text-neutral-400" />
              <div>
                <p className="text-2xl font-medium leading-none">{stats.products}</p>
                <p className="text-[11px] tracking-widest text-neutral-500 mt-1">מוצרים</p>
              </div>
            </div>
            <div className="bg-white border border-neutral-200 p-4 flex items-center gap-3" data-testid="stat-on-sale">
              <Percent className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-medium leading-none">{stats.onSale}</p>
                <p className="text-[11px] tracking-widest text-neutral-500 mt-1">במבצע</p>
              </div>
            </div>
            <div className="bg-white border border-neutral-200 p-4 flex items-center gap-3" data-testid="stat-sold-out">
              <PackageX className="h-5 w-5 text-neutral-400" />
              <div>
                <p className="text-2xl font-medium leading-none">{stats.soldOut}</p>
                <p className="text-[11px] tracking-widest text-neutral-500 mt-1">אזל מהמלאי</p>
              </div>
            </div>
            <div className="bg-white border border-neutral-200 p-4 flex items-center gap-3" data-testid="stat-subscribers">
              <Mail className="h-5 w-5 text-neutral-400" />
              <div>
                <p className="text-2xl font-medium leading-none">{stats.subscribers}</p>
                <p className="text-[11px] tracking-widest text-neutral-500 mt-1">נרשמים לניוזלטר</p>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl tracking-wide">Products</h1>
            <p className="text-sm text-neutral-500 mt-1">{products ? `${products.length} items in your store` : "Loading..."}</p>
          </div>
          <button onClick={openNew} className="flex items-center gap-2 bg-black text-white px-5 py-2.5 text-xs tracking-[0.2em] hover:bg-neutral-800 transition-colors">
            <Plus className="h-4 w-4" /> ADD PRODUCT
          </button>
        </div>

        <div className="bg-white border border-neutral-200 overflow-hidden">
          {!products ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
          ) : products.length === 0 ? (
            <p className="py-16 text-center text-sm text-neutral-500">No products yet. Click "Add product" to begin.</p>
          ) : (
            products.map((p, index) => (
              <div key={p.id} className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100 last:border-0">
                {/* Reorder controls */}
                <div className="flex flex-col items-center shrink-0">
                  <button onClick={() => moveProduct(index, -1)} disabled={index === 0} className="text-neutral-400 hover:text-black disabled:opacity-25" aria-label="up" title="העלה">
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <span className="text-[10px] text-neutral-400">{index + 1}</span>
                  <button onClick={() => moveProduct(index, 1)} disabled={index === products.length - 1} className="text-neutral-400 hover:text-black disabled:opacity-25" aria-label="down" title="הורד">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                <div className="h-16 w-12 bg-neutral-100 overflow-hidden shrink-0">
                  {p.images[0] && <img src={absUrl(p.images[0])} alt={p.name} className="h-full w-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-xs text-neutral-500 capitalize">
                    {p.category} · ₪{Number(p.price).toFixed(2)}
                    {p.tags?.length ? ` · ${p.tags.join(", ")}` : ""}
                    {p.soldOut ? " · SOLD OUT" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveToTop(index)} disabled={index === 0} className="p-2 text-neutral-500 hover:text-black disabled:opacity-25" aria-label="move to top" title="העלה לראש">
                    <ArrowUpToLine className="h-4 w-4" />
                  </button>
                  <button onClick={() => openEdit(p)} className="p-2 text-neutral-500 hover:text-black" aria-label="edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => removeProduct(p.id)} className="p-2 text-neutral-500 hover:text-red-600" aria-label="delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showForm && (
        <ProductForm initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={onSaved} />
      )}
      {showSettings && <SettingsForm onClose={() => setShowSettings(false)} />}
      {showSubs && <SubscribersPanel onClose={() => setShowSubs(false)} />}
    </div>
  );
}
